import { useMemo } from 'react';
import { CursorValue, Platform, type ViewStyle } from 'react-native';

import Colors, { RevampedThemeColors } from '@lib/constants/Colors';
import {
    Breakable,
    getBreakValue,
    isBreakable,
    useBreakpoint,
    useBreakpoints
} from '@lib/hooks/useBreakpoints';

export const ZIndices = {
    page: 0,
    menu: 100,
    drawer: 200,
    modal: 300,
    tooltip: 400
} as const;

type NamedZIndex = keyof typeof ZIndices;
/**
 * TODO: change this to WebMeasurement and enforce with `web` prefix.
 *
 * This type is used for dimensions. For web only cmoponents, it
 * allows you to use unsupported measurements, such as `vw` and `vh`.
 *
 * The Mesurement type are available for shorthand properties,
 *  e.g. `my` `px`, `w` etc.
 */
export type Measurement =
    | number
    | `${string}%`
    | `${string}vh`
    | `${string}vw`
    | `${string}px`
    | 'fit-content'
    | `calc(${string})`
    | 'auto';

export interface StyleShorthand extends Omit<ViewStyle, 'position' | 'overflow'> {
    /**
     * Shorthand for alignItems: center, justifyContent: space-around
     */
    center?: boolean;
    /**
     * flexDirection: row
     */
    row?: boolean;
    /**
     * flexDirection: column (default true)
     */
    column?: boolean;
    /**
     * flexShrink: shrink (default 0)
     */
    shrink?: number;
    /**
     * TODO: name these like z indices
     *
     * 0 - no shadow
     * 1 - flat spread
     * 2 - drop shadow below and to the right
     * 3 - wide (16px) drop sligtly below and to the right
     */
    shadow?: keyof typeof NamedShadows | number;
    /**
     * render with 1:1 aspect ratio
     */
    square?: boolean;
    /**
     * render with 7:5 aspect ratio
     */
    postcard?: boolean;
    /**
     * render with 4:3 aspect ratio
     */
    photo?: boolean;
    /**
     * render with 16:9 aspect ratio
     */
    letterbox?: boolean;
    scrollx?: boolean;
    scrolly?: boolean;
    /**
     * web only
     */
    scrollbarWidth?: Measurement | 'none';
    /**
     * web only
     */
    cursor?: CursorValue;
    userSelect?: 'all' | 'text' | 'auto' | 'contain' | 'none';

    /**
     * web only
     */
    gridTemplateColumns?: string;

    /**
     * web only - CSS transition properties
     */
    transitionDuration?: string;
    transitionTimingFunction?: string;
    transitionProperty?: string;
    transition?: string;

    gap?: number;
    position?: 'fixed' | 'absolute' | 'relative' | 'static' | 'sticky';
    overflow?: 'scroll' | 'visible' | 'hidden' | 'auto';
    outlineColor?: string;
    /**
     * use zIndex names (`NamedZIndex`) or the zIndex as a number
     */
    z?: NamedZIndex | number;
    w?: Measurement;
    h?: Measurement;
    p?: Measurement;
    pt?: Measurement;
    pb?: Measurement;
    pl?: Measurement;
    pr?: Measurement;
    px?: Measurement;
    py?: Measurement;
    m?: Measurement;
    mt?: Measurement;
    mb?: Measurement;
    ml?: Measurement;
    mr?: Measurement;
    mx?: Measurement;
    my?: Measurement;
    /**
     * shorthand for maxWidth
     */
    mw?: Measurement;
    /**
     * shorthand for minWidth
     */
    mnw?: Measurement;
    /**
     * shorthand for maxHeight
     */
    mh?: Measurement;
    /**
     * shorthand for minHeight
     */
    mnh?: Measurement;
}

type StyleShorthandKey = keyof StyleShorthand;

// compiler hint so the values don't get cast to `string`s
const CenteredFlex = { alignItems: 'center', justifyContent: 'space-around' } as const;
const RowFlex = { flexDirection: 'row' } as const;
const ShrinkFlex = { flexShrink: 0 } as const;
const ColumnFlex = { flexDirection: 'column' } as const;
const SquareAspectRatio = { aspectRatio: 1 } as const;
const PhotoAspectRatio = { aspectRatio: 4 / 3 } as const;
const PostcardAspectRatio = { aspectRatio: 7 / 5 } as const;
const LetterboxAspectRatio = { aspectRatio: 16 / 9 } as const;
const ScrollX = { overflowX: 'scroll' } as const;
const ScrollY = { overflowY: 'scroll' } as const;

const Shadows = [
    {},
    // flat spread
    {
        shadowColor: 'black',
        shadowOffset: {
            width: 0,
            height: 0
        },
        shadowOpacity: 0.5,
        shadowRadius: 6,

        elevation: 3
    },
    {
        shadowColor: 'primaryDark',
        shadowOffset: {
            width: 0,
            height: 2
        },
        shadowOpacity: 0.2,
        shadowRadius: 4,

        elevation: 3
    },
    {
        shadowColor: 'black',
        shadowOffset: {
            width: 2,
            height: 2
        },
        shadowOpacity: 0.25,
        shadowRadius: 16,

        elevation: 3
    }
] as const;

const NamedShadows = {
    none: Shadows[0],
    flat: Shadows[1],
    drop: Shadows[2],
    button: Shadows[3]
} as const;

// sanitize functions to normalize web css properties to their native fallbacks

const sanitizePosition = (
    position: 'fixed' | 'static' | 'sticky' | 'absolute' | 'relative' | undefined
): ViewStyle['position'] => {
    if (Platform.OS === 'web') {
        return position as any;
    }

    if (position === 'fixed' || position === 'sticky') {
        return 'absolute';
    }

    if (position === 'static') {
        return 'relative';
    }
    return position;
};

const sanitizeOverflow = (
    overflow: 'scroll' | 'visible' | 'hidden' | 'auto' | undefined
): ViewStyle['overflow'] => {
    if (Platform.OS === 'web') {
        return overflow as any;
    }

    if (overflow === 'auto') {
        return 'scroll';
    }

    return overflow;
};

const toStyle = (
    {
        position,
        overflow,
        center,
        row,
        shrink,
        column,
        shadow,
        square,
        photo,
        postcard,
        letterbox,
        scrollx,
        scrolly,
        z,
        w,
        h,
        p,
        pt,
        pb,
        pl,
        pr,
        px,
        py,
        m,
        mt,
        mb,
        ml,
        mr,
        mx,
        my,
        mw,
        mnw,
        mh,
        mnh,
        ...rest
    }: StyleShorthand,
    isUiRevampEnabled = true
): ViewStyle => {
    const expanded = {
        width: w,
        height: h,
        padding: p,
        paddingTop: pt,
        paddingBottom: pb,
        paddingLeft: pl,
        paddingRight: pr,
        paddingHorizontal: px,
        paddingVertical: py,
        margin: m,
        marginTop: mt,
        marginBottom: mb,
        marginLeft: ml,
        marginRight: mr,
        marginHorizontal: mx,
        marginVertical: my,
        maxWidth: mw,
        minWidth: mnw,
        maxHeight: mh,
        minHeight: mnh,
        overflow: sanitizeOverflow(overflow),
        position: sanitizePosition(position),
        ...(center ? CenteredFlex : {}),
        ...(row ? RowFlex : {}),
        ...(shrink ? ShrinkFlex : {}),
        ...(column ? ColumnFlex : {}),
        ...(square ? SquareAspectRatio : {}),
        ...(photo ? PhotoAspectRatio : {}),
        ...(postcard ? PostcardAspectRatio : {}),
        ...(letterbox ? LetterboxAspectRatio : {}),
        ...(scrollx ? ScrollX : {}),
        ...(scrolly ? ScrollY : {}),
        ...(typeof shadow === 'string' ? NamedShadows[shadow] : (Shadows[shadow ?? 0] ?? {})),
        ...(z ? { elevation: typeof z === 'number' ? z : ZIndices[z] } : {}),
        ...(z ? { zIndex: typeof z === 'number' ? z : ZIndices[z] } : {}),
        ...rest
    };

    let key: keyof typeof expanded;

    for (key in expanded) {
        if (expanded[key] === undefined) {
            delete expanded[key];
        }
    }

    for (key in expanded) {
        if (!key.endsWith('Color')) {
            continue;
        }

        const value = expanded[key];

        if (typeof value !== 'string') {
            continue;
        }

        if (value in Colors) {
            // typescript just can't quite handle this one.
            // @ts-ignore
            expanded[key] = isUiRevampEnabled ? RevampedThemeColors[value] : Colors[value];
        }
    }

    // HERE BE DRAGONS
    //  - this cast is necesary (and temporary)
    //  - React Native `DimensionValue` has started complaining about dimensions
    //  - We need to plan a tech debt story to fix these.
    return expanded as ViewStyle;
};

type ResponsiveShorthandValue<K extends StyleShorthandKey> = Breakable<StyleShorthand[K]>;

export type ResponsiveShorthand = {
    [K in keyof StyleShorthand]: ResponsiveShorthandValue<K> | StyleShorthand[K];
};

export type Sx = Breakable<StyleShorthand> | StyleShorthand;

export interface SxProps {
    sx?: Sx;
}

export const useShorthandStyles = (props: ResponsiveShorthand) => {
    const breakpoint = useBreakpoint();
    return useMemo(() => {
        const normalized: StyleShorthand = {};
        let prop: StyleShorthandKey;

        for (prop in props) {
            type PropValue = StyleShorthand[typeof prop];
            const propValue: PropValue | Breakable<PropValue> = props[prop];

            // typescript passes this just fine, which is equivalent but less performant.
            //
            // normalized = {
            //     ...normalized,
            //     [prop]: isBreakable(propValue) ? getBreakValue(propValue as any, breakpoint) : propValue,
            // };
            //
            // @ts-ignore
            normalized[prop] = isBreakable(propValue)
                ? getBreakValue(propValue, breakpoint)
                : propValue;
        }

        return toStyle(normalized, true);
    }, [breakpoint, props]);
};

export const useSx = (sx: Sx = {}): ViewStyle => {
    const style = useBreakpoints<StyleShorthand>(sx);
    return toStyle(style, true);
};

export default useShorthandStyles;
